import { DIALOG_COPY } from '@/locales/en'
import { AccountType } from '@/common/types'
import { ApplicationProgressBar } from './ProgressBar'
import { H2 } from '@/common/ui/h2'
import useUserStore from '@/user/userStore'
import useAccountStore from '@/account/accountStore'
import { useCurrentAccount } from '@/account/useCurrentAccount'
import { usePrimaryAccount } from '@/account/usePrimaryAccount'
import { Button } from '@/common/ui/button'
import { useFetchKYC } from '@/kyc/kycQueries'
import { useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { FullScreenLoader } from '@/common/ui/FullScreenLoader'
import { APP_ROUTES } from '@/common/constants'
import { SwiftIdResumeContent } from './SwfitIdResume'
import { KycProviders } from '@/kyc/types'
import { analytics } from '@/common/analytics/analytics'
import { resolveApplicationResumeView, APPLICATION_RESUME_VIEW } from '@/kyc/kycFlowResolver'
import { resolveHomeRouteForContext } from '@/common/navigation/accountRoutes'

const FrankieOneResumeContent = ({
    onContinue,
    isPrimaryAdviserUser,
}: {
    onContinue: () => void
    isPrimaryAdviserUser: boolean
}) => {
    const noKycCopy = isPrimaryAdviserUser ? DIALOG_COPY.noKycAdviser : DIALOG_COPY.noKyc

    return (
        <div className="w-full flex flex-col justify-start items-start gap-6">
            <H2 className="justify-start mb-0">{noKycCopy.title}</H2>
            <div className="justify-start text-black text-sm font-normal font-['Inter'] leading-tight">
                {noKycCopy.description.split('\n\n').map((desc, idx, arr) => (
                    <span key={idx}>
                        {desc}
                        {idx < arr.length - 1 && (
                            <>
                                <br />
                                <br />
                            </>
                        )}
                    </span>
                ))}
            </div>
            <div className="w-full flex justify-end items-end mt-4">
                <Button variant="secondary" className="h-10 px-6 py-2.5 border-0" onClick={onContinue}>
                    {noKycCopy.primaryButton}
                </Button>
            </div>
        </div>
    )
}

export const ApplicationResumePage = () => {
    const { user } = useUserStore()
    const account = useCurrentAccount()
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const primaryAccount = usePrimaryAccount()
    const { data: kyc } = useFetchKYC(activeAccountId, { enabled: true })
    const navigate = useNavigate()
    const isPrimaryAdviserUser = primaryAccount?.accountType === AccountType.ADVISER

    const resumeView = useMemo(
        () =>
            resolveApplicationResumeView({
                kyc,
                userId: user?.userId,
            }).view,
        [kyc, user?.userId],
    )

    const goToKycConfirm = useCallback(() => {
        const accountType = account?.accountType ?? user?.accountType
        if (!accountType || !activeAccountId) {
            navigate(
                resolveHomeRouteForContext({
                    primaryAccount: primaryAccount ?? null,
                    activeAccountId,
                }),
            )
            return
        }

        navigate(APP_ROUTES.APPLICATION_KYC_CONFIRM, {
            state: {
                accountType,
                createdAccountId: activeAccountId,
                isAdviser: isPrimaryAdviserUser,
            },
        })
    }, [account?.accountType, activeAccountId, isPrimaryAdviserUser, navigate, primaryAccount, user?.accountType])

    useEffect(() => {
        if (!user) return
        if (!kyc) {
            goToKycConfirm()
            return
        }
        if (!kyc.canInitiateKyc) {
            navigate(
                resolveHomeRouteForContext({
                    primaryAccount: primaryAccount ?? null,
                    activeAccountId,
                }),
            )
            return
        }
        if (kyc.provider === KycProviders.SWIFTID && kyc.status === 'UNVERIFIED') {
            goToKycConfirm()
            return
        }
        if (!kyc.provider) {
            goToKycConfirm()
        }
    }, [activeAccountId, goToKycConfirm, kyc, navigate, primaryAccount, user])

    const goToKYC = () => {
        analytics.trackWithDefaults('application_resumed', {
            resume_reason: 'resumed from menu',
            account_type: account?.accountType ?? user?.accountType,
            resume_view: resumeView.toLowerCase(),
        })
        navigate(APP_ROUTES.APPLICATION_KYC)
    }

    if (!user || !kyc) {
        return <FullScreenLoader />
    }

    return (
        <>
            <div className="w-full h-full flex flex-col justify-start items-center min-h-0">
                <div className="w-full flex-1 overflow-auto flex flex-col justify-center items-center gap-12">
                    <div className="flex w-full max-w-[600px] flex-col justify-center items-center gap-12">
                        <ApplicationProgressBar active={2} />
                        {kyc.provider === KycProviders.SWIFTID && (
                            <SwiftIdResumeContent user={user} account={account} kyc={kyc} />
                        )}
                        {kyc.provider === KycProviders.FRANKIEONE &&
                            resumeView === APPLICATION_RESUME_VIEW.PARTICIPANT && (
                                <FrankieOneResumeContent
                                    onContinue={goToKYC}
                                    isPrimaryAdviserUser={isPrimaryAdviserUser}
                                />
                            )}
                        {kyc.provider === KycProviders.FRANKIEONE && resumeView === APPLICATION_RESUME_VIEW.ACCOUNT && (
                            <FrankieOneResumeContent onContinue={goToKYC} isPrimaryAdviserUser={isPrimaryAdviserUser} />
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}
